// client/src/components/event/AuctionConfig.jsx
/**
 * =================================================================
 * Auction Configuration Component - FIXED
 * =================================================================
 * FEATURES:
 * 1. Set number of winners
 * 2. Set starting bid amount
 * 3. Schedule start/end date and time
 * 4. Display shareable hyperlink (copy-pasteable)
 * 5. View current bids and statistics
 * 6. Manual winner selection (for testing/admin)
 * 
 * FIXES:
 * - Properly handles 404 (no auction exists yet)
 * - Uses correct field names (winners_to_pick, not winners_per_combo)
 * - Works with datetime-local inputs
 * =================================================================
 */

import React, { useState, useEffect } from "react";
import axios from "axios";
import "./AuctionConfig.css";

const AuctionConfig = ({ tenantId, eventId, itemId, itemName, onClose }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ text: "", type: "" });

  // Auction configuration state
  const [config, setConfig] = useState({
    name: itemName || "Auction",
    winners_to_pick: 1,
    starting_bid: "",
    start_at: "",
    end_at: "",
    status: "NEW",
    active: 1,
  });

  // Auction statistics
  const [stats, setStats] = useState(null);
  const [shareLink, setShareLink] = useState("");
  const [entries, setEntries] = useState([]);
  const [winners, setWinners] = useState([]);
  const [showEntries, setShowEntries] = useState(false);
  const [showWinners, setShowWinners] = useState(false);

  const baseUrl = process.env.REACT_APP_BASE_URL || "";

  useEffect(() => {
    loadAuctionConfig();
  }, [tenantId, eventId, itemId]);

  /**
   * Format MySQL datetime for datetime-local input
   */
  const formatDateForInput = (dateStr) => {
    if (!dateStr) return "";
    try {
      const date = new Date(dateStr);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      return `${year}-${month}-${day}T${hours}:${minutes}`;
    } catch (e) {
      console.error("Error formatting date:", e);
      return "";
    }
  };

  /**
   * Format datetime-local input for backend (MySQL format)
   */
  const formatDateTimeForBackend = (datetimeLocalValue) => {
    if (!datetimeLocalValue) return null;
    
    try {
      // datetime-local input gives us: "2025-12-19T23:59"
      // We need MySQL format: "2025-12-19 23:59:00"
      const date = new Date(datetimeLocalValue);
      
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      const seconds = String(date.getSeconds()).padStart(2, '0');
      
      return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
    } catch (e) {
      console.error("Error formatting datetime:", e);
      return null;
    }
  };

  /**
   * Load existing auction configuration
   * FIXED: Properly handles 404 when no auction exists yet
   */
  const loadAuctionConfig = async () => {
    setLoading(true);
    setMessage({ text: "", type: "" });
    
    try {
      const response = await axios.get(
        `${baseUrl}/api/auction/${tenantId}/event/${eventId}/item/${itemId}`
      );

      if (response.data.success) {
        const auction = response.data.auction;
        
        setConfig({
          name: auction.name || itemName || "Auction",
          winners_to_pick: auction.winners_to_pick || 1,
          starting_bid: auction.starting_bid !== null ? auction.starting_bid : "",
          start_at: formatDateForInput(auction.start_at),
          end_at: formatDateForInput(auction.end_at),
          status: auction.status || "NEW",
          active: auction.active || 1,
        });

        setStats(auction.stats);
        setShareLink(auction.shareLink);

        // If auction is complete, load winners
        if (auction.status === "COMPLETE") {
          loadWinners();
        }
      }
    } catch (err) {
      console.error("Error loading auction config:", err);
      
      // FIXED: Handle 404 properly - it means no auction exists yet
      if (err.response?.status === 404) {
        setMessage({
          text: "No auction configured yet. Fill out the form below to create one.",
          type: "info",
        });
      } else {
        setMessage({
          text: "Failed to load auction configuration",
          type: "error",
        });
      }
    } finally {
      setLoading(false);
    }
  };

  /**
   * Load bid entries
   */
  const loadEntries = async () => {
    try {
      const response = await axios.get(
        `${baseUrl}/api/auction/${tenantId}/event/${eventId}/item/${itemId}/entries`
      );

      if (response.data.success) {
        setEntries(response.data.entries);
        setShowEntries(true);
      }
    } catch (err) {
      console.error("Error loading entries:", err);
      setMessage({
        text: "Failed to load bid entries",
        type: "error",
      });
    }
  };

  /**
   * Load winners
   */
  const loadWinners = async () => {
    try {
      const response = await axios.get(
        `${baseUrl}/api/auction/${tenantId}/event/${eventId}/item/${itemId}/winners`
      );

      if (response.data.success) {
        setWinners(response.data.winners);
        setShowWinners(true);
      }
    } catch (err) {
      console.error("Error loading winners:", err);
      setMessage({
        text: "Failed to load winners",
        type: "error",
      });
    }
  };

  /**
   * Handle form input changes
   */
  const handleInputChange = (field, value) => {
    setConfig((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  /**
   * Validate configuration
   */
  const validateConfig = () => {
    const errors = [];

    if (!config.name || config.name.trim().length === 0) {
      errors.push("Auction name is required");
    }

    if (config.winners_to_pick < 1 || config.winners_to_pick > 100) {
      errors.push("Winners to pick must be between 1 and 100");
    }

    if (config.starting_bid !== "" && (isNaN(config.starting_bid) || Number(config.starting_bid) < 0)) {
      errors.push("Starting bid must be a non-negative number");
    }

    if (config.start_at && config.end_at) {
      const start = new Date(config.start_at);
      const end = new Date(config.end_at);
      if (end <= start) {
        errors.push("End date must be after start date");
      }
    }

    return errors;
  };

  /**
   * Save auction configuration
   * FIXED: Uses correct field name (winners_to_pick)
   */
  const handleSave = async (e) => {
    e.preventDefault();

    const errors = validateConfig();
    if (errors.length > 0) {
      setMessage({
        text: errors.join(". "),
        type: "error",
      });
      return;
    }

    setSaving(true);
    setMessage({ text: "", type: "" });

    try {
      const payload = {
        name: config.name,
        winners_to_pick: Number(config.winners_to_pick),
        starting_bid: config.starting_bid !== "" ? Number(config.starting_bid) : null,
        start_at: formatDateTimeForBackend(config.start_at),
        end_at: formatDateTimeForBackend(config.end_at),
        status: config.status,
        active: config.active,
      };

      const response = await axios.post(
        `${baseUrl}/api/auction/${tenantId}/event/${eventId}/item/${itemId}/config`,
        payload
      );

      if (response.data.success) {
        setMessage({
          text: "Auction configuration saved successfully!",
          type: "success",
        });

        // Update share link
        if (response.data.auction && response.data.auction.shareLink) {
          setShareLink(response.data.auction.shareLink);
        }

        // Reload to get latest stats
        setTimeout(() => {
          setMessage({ text: "", type: "" });
          loadAuctionConfig();
        }, 2000);
      }
    } catch (err) {
      console.error("Error saving auction config:", err);
      setMessage({
        text: err.response?.data?.message || "Failed to save auction configuration",
        type: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  /**
   * Copy share link to clipboard
   */
  const copyShareLink = () => {
    if (!shareLink) return;

    navigator.clipboard.writeText(shareLink).then(
      () => {
        setMessage({
          text: "Link copied to clipboard!",
          type: "success",
        });
        setTimeout(() => setMessage({ text: "", type: "" }), 3000);
      },
      (err) => {
        console.error("Failed to copy:", err);
        setMessage({
          text: "Failed to copy link",
          type: "error",
        });
      }
    );
  };

  /**
   * Award winners (manual selection for testing/admin)
   */
  const handleAwardWinners = async () => {
    if (!window.confirm("Award winners now? This will select the top bidders based on your configuration.")) {
      return;
    }

    setSaving(true);
    setMessage({ text: "", type: "" });

    try {
      const response = await axios.post(
        `${baseUrl}/api/auction/${tenantId}/event/${eventId}/item/${itemId}/award`
      );

      if (response.data.success) {
        setMessage({
          text: `${response.data.count} winner(s) selected!`,
          type: "success",
        });

        // Reload configuration and winners
        setTimeout(() => {
          loadAuctionConfig();
          loadWinners();
        }, 1000);
      }
    } catch (err) {
      console.error("Error awarding winners:", err);
      setMessage({
        text: err.response?.data?.message || "Failed to award winners",
        type: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  /**
   * Delete auction configuration
   */
  const handleDelete = async () => {
    if (!window.confirm("Delete this auction? This cannot be undone.")) {
      return;
    }

    setSaving(true);
    setMessage({ text: "", type: "" });

    try {
      const response = await axios.delete(
        `${baseUrl}/api/auction/${tenantId}/event/${eventId}/item/${itemId}`
      );

      if (response.data.success) {
        setMessage({
          text: "Auction deleted successfully",
          type: "success",
        });

        // Reset to default state
        setTimeout(() => {
          setConfig({
            name: itemName || "Auction",
            winners_to_pick: 1,
            starting_bid: "",
            start_at: "",
            end_at: "",
            status: "NEW",
            active: 1,
          });
          setStats(null);
          setShareLink("");
          setMessage({ text: "", type: "" });
        }, 2000);
      }
    } catch (err) {
      console.error("Error deleting auction:", err);
      setMessage({
        text: err.response?.data?.message || "Failed to delete auction",
        type: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="auction-config-container">
        <div className="loading">Loading auction configuration...</div>
      </div>
    );
  }

  const isComplete = config.status === "COMPLETE";

  return (
    <div className="auction-config-container">
      {/* Header */}
      <div className="auction-config-header">
        <h2>Auction Configuration</h2>
        <button
          className="close-button"
          onClick={onClose}
          aria-label="Close"
        >
          &times;
        </button>
      </div>

      {/* Messages */}
      {message.text && (
        <div className={`message message-${message.type}`}>
          {message.text}
        </div>
      )}

      {/* Share Link Section - only show if auction exists */}
      {shareLink && (
        <div className="share-link-section">
          <h3>Shareable Link</h3>
          <p className="share-link-description">
            Share this link with participants to allow them to submit bids:
          </p>
          <div className="share-link-container">
            <div className="share-link-display">
              <a href={shareLink} target="_blank" rel="noopener noreferrer">
                {shareLink}
              </a>
            </div>
            <button
              type="button"
              className="copy-link-button"
              onClick={copyShareLink}
            >
              Copy Link
            </button>
          </div>
        </div>
      )}

      {/* Statistics Section - only show if auction exists */}
      {stats && (
        <div className="auction-stats-section">
          <h3>Auction Statistics</h3>
          <div className="stats-grid">
            <div className="stat-item">
              <div className="stat-label">Total Bids</div>
              <div className="stat-value">{stats.entry_count || 0}</div>
            </div>
            <div className="stat-item">
              <div className="stat-label">Highest Bid</div>
              <div className="stat-value">
                {stats.highest_bid !== null
                  ? `$${Number(stats.highest_bid).toFixed(2)}`
                  : "—"}
              </div>
            </div>
            <div className="stat-item">
              <div className="stat-label">Lowest Bid</div>
              <div className="stat-value">
                {stats.lowest_bid !== null
                  ? `$${Number(stats.lowest_bid).toFixed(2)}`
                  : "—"}
              </div>
            </div>
            <div className="stat-item">
              <div className="stat-label">Average Bid</div>
              <div className="stat-value">
                {stats.average_bid !== null
                  ? `$${Number(stats.average_bid).toFixed(2)}`
                  : "—"}
              </div>
            </div>
          </div>

          <div className="stats-actions">
            <button
              type="button"
              className="view-entries-button"
              onClick={loadEntries}
            >
              View All Bids
            </button>
            {isComplete && (
              <button
                type="button"
                className="view-winners-button"
                onClick={loadWinners}
              >
                View Winners
              </button>
            )}
          </div>
        </div>
      )}

      {/* Configuration Form */}
      <form className="auction-config-form" onSubmit={handleSave}>
        {/* Auction Name */}
        <div className="form-group">
          <label htmlFor="name">
            Auction Name / Item Name <span className="required">*</span>
          </label>
          <input
            type="text"
            id="name"
            value={config.name}
            onChange={(e) => handleInputChange("name", e.target.value)}
            placeholder="Enter auction name"
            disabled={isComplete}
            required
          />
        </div>

        {/* Winners to Pick */}
        <div className="form-group">
          <label htmlFor="winners_to_pick">
            Number of Winners <span className="required">*</span>
          </label>
          <input
            type="number"
            id="winners_to_pick"
            min="1"
            max="100"
            value={config.winners_to_pick}
            onChange={(e) =>
              handleInputChange("winners_to_pick", parseInt(e.target.value) || 1)
            }
            disabled={isComplete}
            required
          />
          <span className="form-hint">
            Number of winners to select (top bidders)
          </span>
        </div>

        {/* Starting Bid */}
        <div className="form-group">
          <label htmlFor="starting_bid">Starting Bid Amount</label>
          <input
            type="number"
            id="starting_bid"
            min="0"
            step="0.01"
            value={config.starting_bid}
            onChange={(e) => handleInputChange("starting_bid", e.target.value)}
            placeholder="Optional minimum bid"
            disabled={isComplete}
          />
          <span className="form-hint">
            Minimum bid amount (leave empty for no minimum)
          </span>
        </div>

        {/* Date/Time Range */}
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="start_at">Start Date & Time</label>
            <input
              type="datetime-local"
              id="start_at"
              value={config.start_at}
              onChange={(e) => handleInputChange("start_at", e.target.value)}
              disabled={isComplete}
            />
            <span className="form-hint">When bidding opens</span>
          </div>

          <div className="form-group">
            <label htmlFor="end_at">End Date & Time</label>
            <input
              type="datetime-local"
              id="end_at"
              value={config.end_at}
              onChange={(e) => handleInputChange("end_at", e.target.value)}
              disabled={isComplete}
            />
            <span className="form-hint">When bidding closes</span>
          </div>
        </div>

        {/* Status */}
        <div className="form-group">
          <label htmlFor="status">Status</label>
          <select
            id="status"
            value={config.status}
            onChange={(e) => handleInputChange("status", e.target.value)}
            disabled={isComplete}
          >
            <option value="NEW">New</option>
            <option value="ACTIVE">Active</option>
            <option value="PROCESSING">Processing</option>
            <option value="COMPLETE">Complete</option>
            <option value="CANCELLED">Cancelled</option>
          </select>
        </div>

        {/* Form Actions */}
        <div className="form-actions">
          {isComplete ? (
            <div className="completion-notice">
              Auction is complete - no further changes allowed
            </div>
          ) : (
            <>
              <button
                type="submit"
                className="save-button"
                disabled={saving}
              >
                {saving ? "Saving..." : "Save Configuration"}
              </button>

              {stats && stats.entry_count > 0 && (
                <button
                  type="button"
                  className="award-button"
                  onClick={handleAwardWinners}
                  disabled={saving}
                >
                  Award Winners
                </button>
              )}

              {shareLink && (
                <button
                  type="button"
                  className="delete-button"
                  onClick={handleDelete}
                  disabled={saving}
                >
                  Delete Auction
                </button>
              )}
            </>
          )}
        </div>
      </form>

      {/* Entries Modal */}
      {showEntries && (
        <div className="modal-overlay" onClick={() => setShowEntries(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>All Bids ({entries.length})</h3>
              <button
                className="close-button"
                onClick={() => setShowEntries(false)}
              >
                &times;
              </button>
            </div>
            <div className="modal-body">
              <table className="entries-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Phone</th>
                    <th>Bid Amount</th>
                    <th>Date</th>
                  </tr>
                </thead>
                <tbody>
                  {entries.map((entry, index) => (
                    <tr key={entry.id}>
                      <td data-label="Name">{entry.name}</td>
                      <td data-label="Email">{entry.email}</td>
                      <td data-label="Phone">{entry.phone || "—"}</td>
                      <td className="bid-amount" data-label="Bid Amount">
                        ${Number(entry.bidamount).toFixed(2)}
                      </td>
                      <td data-label="Date">{new Date(entry.created_on).toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Winners Modal */}
      {showWinners && (
        <div className="modal-overlay" onClick={() => setShowWinners(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Winners ({winners.length})</h3>
              <button
                className="close-button"
                onClick={() => setShowWinners(false)}
              >
                &times;
              </button>
            </div>
            <div className="modal-body">
              <table className="winners-table">
                <thead>
                  <tr>
                    <th>Place</th>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Phone</th>
                    <th>Winning Bid</th>
                    <th>Email Status</th>
                  </tr>
                </thead>
                <tbody>
                  {winners.map((winner, index) => (
                    <tr key={winner.id} className="winner-row">
                      <td className="place-cell" data-label="Place">
                        {index === 0
                          ? "🥇 1st"
                          : index === 1
                          ? "🥈 2nd"
                          : index === 2
                          ? "🥉 3rd"
                          : `${index + 1}th`}
                      </td>
                      <td data-label="Name">{winner.name}</td>
                      <td data-label="Email">{winner.email}</td>
                      <td data-label="Phone">{winner.phone || "—"}</td>
                      <td className="bid-amount" data-label="Winning Bid">
                        ${Number(winner.bidamount).toFixed(2)}
                      </td>
                      <td data-label="Email Status">
                        <span className={`email-status ${winner.winner_notified ? 'sent' : 'pending'}`}>
                          {winner.winner_notified ? '✅ Sent' : '⏳ Pending'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AuctionConfig;